import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    CountryDTO,
    useDeleteCountryApi,
    useSaveCountryApi,
    useSearchCountryApi,
} from '../../../api/country-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveCountryModal } from './save-country-modal';

export function CountryApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<CountryDTO | undefined>();
    const countryInit = useSearchCountryApi();
    const saveCountry = useSaveCountryApi();
    const deleteCountry = useDeleteCountryApi();

    const { result, error, busy, initialized, initializing } = countryInit;

    const columns: IBasicTableColumn<CountryDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
            },
            {
                Header: <T>name</T>,
                name: 'name',
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        countryInit.post(null, {});
    }, [initialized, initializing, error, countryInit]);

    const handleDelete = (country: CountryDTO) => {
        bd.msgbox(
            translate('delete-country'),
            translate('confirm-delete-country'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteCountry.remove(
                            country,
                            () => {
                                countryInit.updateResult({
                                    ...result,
                                    countries: result.countries.filter(b => b.id !== country.id),
                                });
                                notify.dark(translate('selected-country-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedCountry(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (country: CountryDTO) => {
        saveCountry.post(
            null,
            country,
            res => {
                if (selectedCountry) {
                    countryInit.updateResult({
                        ...result,
                        countries: result.countries.map(b =>
                            b.id === country.id ? res.country : b
                        ),
                    });
                } else {
                    countryInit.updateResult({
                        ...result,
                        countries: [res.country, ...result.countries],
                    });
                }

                setShowModal(false);
                setSelectedCountry(undefined);
                notify.success(
                    selectedCountry
                        ? translate('selected-country-updated-successfully')
                        : translate('new-country-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => countryInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="countries"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-country</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedCountry(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.countries}
                        onRowClick={({ row }) => {
                            setSelectedCountry(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveCountryModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedCountry(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedCountry={selectedCountry}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
